import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { StepperHorizontal } from '@ibk/shared-layout';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { Alert, Breadcrumb } from '@ibk/shared-ui';
import { BilletGeneratedStep } from '../../components/DepositBilletSteps/BilletGeneratedStep/BilletGeneratedStep';
import { ValueStep } from '../../components/DepositBilletSteps/ValueStep/ValueStep';
import { useCreateDepositBilletMutation } from '../../services/receiptApi';
import { pagamentosTranslations } from '../../translations';
import { mapDepositBilletErrorToMessage } from '../../utils/errorMessages';
import styles from './DepositBillet.module.css';
export function DepositBilletBase({ dataTestId = 'deposit-billet', onBack }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isReceiveBilletEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.COREBANKING_ENABLE_RECEIVE_BILLET);
    const [createDepositBillet, { isLoading: isCreatingBillet }] = useCreateDepositBilletMutation();
    const [currentStep, setCurrentStep] = useState(0);
    const [depositData, setDepositData] = useState({
        amount: '',
    });
    const [errorMessage, setErrorMessage] = useState(null);
    useEffect(() => {
        if (!isReceiveBilletEnabled) {
            navigate('/conta-pj/receber');
        }
    }, [isReceiveBilletEnabled, navigate]);
    const steps = [
        {
            title: t('deposit-billet.steps.value'),
        },
        {
            title: t('deposit-billet.steps.billet-generated'),
        },
    ];
    const handleBack = () => {
        if (onBack) {
            onBack();
        }
        else {
            navigate('/conta-pj/receber');
        }
    };
    const handleNextStep = async (data) => {
        setDepositData(data);
        setErrorMessage(null);
        try {
            const numericAmount = parseFloat(data.amount.replace(/[^\d,]/g, '').replace(',', '.'));
            const response = await createDepositBillet({
                amount: numericAmount,
                bankEmissor: 1,
            }).unwrap();
            setDepositData(prev => ({
                ...prev,
                billetCode: response.result.bankslipCode,
                digitableLine: response.result.digitableLine,
                id: response.result.id,
            }));
            setCurrentStep(1);
        }
        catch (error) {
            const errorMessage = mapDepositBilletErrorToMessage(error, t);
            setErrorMessage(errorMessage);
        }
    };
    const breadcrumbItems = [
        {
            label: t('deposit-billet.breadcrumb.to-receive'),
            onClick: handleBack,
            isActive: false,
        },
        {
            label: t('deposit-billet.breadcrumb.deposit-by-billet'),
            isActive: true,
        },
    ];
    const renderCurrentStep = () => {
        switch (currentStep) {
            case 0:
                return (_jsx(ValueStep, { data: depositData, onNext: handleNextStep, onBack: handleBack, isLoading: isCreatingBillet, dataTestId: `${dataTestId}-value-step` }));
            case 1:
                return _jsx(BilletGeneratedStep, { data: depositData, dataTestId: `${dataTestId}-billet-generated-step` });
            default:
                return null;
        }
    };
    if (!isReceiveBilletEnabled) {
        return null;
    }
    return (_jsxs("main", { className: styles.container, "data-testid": dataTestId, children: [errorMessage && (_jsx(Alert, { message: errorMessage, appearance: 'error', alertType: 'toast', density: 'compact', duration: 8, alignItems: 'center', hideCloseButton: false, onClose: () => setErrorMessage(null), "data-testid": `${dataTestId}-error-alert` })), _jsx("div", { className: styles.header, children: _jsx(Breadcrumb, { items: breadcrumbItems, className: styles.breadcrumb }) }), _jsx("div", { className: styles.content, children: _jsxs("div", { className: styles.card, children: [_jsx(StepperHorizontal, { steps: steps, activeStep: currentStep }), _jsx("div", { className: styles.stepContent, children: renderCurrentStep() })] }) })] }));
}
export function DepositBillet(props) {
    return (_jsx(TranslationProvider, { translations: pagamentosTranslations, defaultLanguage: 'pt-BR', children: _jsx(DepositBilletBase, { ...props }) }));
}
